/**
 * Remove interior rings from Polygon / MultiPolygon geometries.
 * Census-derived county merges often keep thousands of “holes” (lakes, reservoirs, bays); Leaflet strokes
 * every ring, which reads as random white lines on choropleths. Keeping only the exterior ring fills those
 * areas with the county/city color and removes spurious outlines.
 */
import type { Feature, FeatureCollection, Geometry, Polygon, MultiPolygon, Position } from 'geojson';

function exteriorOnly(rings: Position[][]): Position[][] {
  if (!rings.length) return rings;
  return [rings[0]];
}

function stripGeometry(g: Geometry): Geometry {
  if (g.type === 'Polygon') {
    const p = g as Polygon;
    return { type: 'Polygon', coordinates: exteriorOnly(p.coordinates) };
  }
  if (g.type === 'MultiPolygon') {
    const m = g as MultiPolygon;
    return {
      type: 'MultiPolygon',
      coordinates: m.coordinates.map((poly) => exteriorOnly(poly)),
    };
  }
  return g;
}

export function stripPolygonHolesFromFeature(fc: Feature): Feature {
  if (!fc.geometry) return fc;
  return { ...fc, geometry: stripGeometry(fc.geometry) };
}

export function stripPolygonHolesFromFeatureCollection(fc: FeatureCollection): FeatureCollection {
  return {
    ...fc,
    features: fc.features.map(stripPolygonHolesFromFeature),
  };
}
